import { useState } from 'react';
import { 
  Download, 
  Upload, 
  Trash2, 
  FileText, 
  Shield, 
  ChevronRight,
  AlertTriangle,
  Users,
  Tag,
  Database
} from 'lucide-react';
import { useStore } from '@/store/useStore';
import { exportMemoriesToPDF, exportMemoriesToZip, downloadJSON, readJSONFile } from '@/utils/fileUtils';

export default function Settings() {
  const { 
    memories, 
    children, 
    tags, 
    settings, 
    exportData, 
    importData, 
    clearAll,
    updateSettings 
  } = useStore();
  
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const handleExportJSON = () => {
    const data = exportData();
    downloadJSON(JSON.parse(data), `家庭时光备份_${new Date().toISOString().split('T')[0]}.json`);
  };

  const handleImportJSON = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await readJSONFile(file);
      const json = JSON.stringify(data);
      
      if (importData(json)) {
        alert('数据导入成功！');
      } else {
        alert('数据格式错误，导入失败');
      }
    } catch {
      alert('读取文件失败');
    }
  };

  const handleClearAll = () => {
    clearAll();
    setShowClearConfirm(false);
  };

  const stats = [
    { label: '记录总数', value: memories.length, icon: Database, color: 'bg-blue-100 text-blue-600' },
    { label: '孩子数量', value: children.length, icon: Users, color: 'bg-green-100 text-green-600' },
    { label: '标签数量', value: tags.length, icon: Tag, color: 'bg-purple-100 text-purple-600' },
    { label: '收藏数量', value: memories.filter(m => m.isFavorite).length, icon: Database, color: 'bg-red-100 text-red-600' },
  ];

  return (
    <div className="px-4 py-4 space-y-4">
      {/* 统计卡片 */}
      <div className="grid grid-cols-2 gap-3">
        {stats.map(stat => (
          <div key={stat.label} className="bg-white rounded-xl p-4 card-shadow">
            <div className={`w-10 h-10 rounded-xl ${stat.color} flex items-center justify-center mb-2`}>
              <stat.icon className="w-5 h-5" />
            </div>
            <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
            <p className="text-xs text-gray-500">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* 数据管理 */}
      <div className="bg-white rounded-2xl overflow-hidden card-shadow">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="font-bold text-gray-800">数据管理</h3>
        </div>
        
        <button
          onClick={handleExportJSON}
          className="w-full flex items-center justify-between px-4 py-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
              <Download className="w-5 h-5" />
            </div>
            <div className="text-left">
              <p className="font-medium text-gray-800">导出数据</p>
              <p className="text-xs text-gray-500">导出为 JSON 文件备份</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </button>

        <label className="w-full flex items-center justify-between px-4 py-4 hover:bg-gray-50 transition-colors cursor-pointer border-t border-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-100 text-green-600 flex items-center justify-center">
              <Upload className="w-5 h-5" />
            </div>
            <div className="text-left">
              <p className="font-medium text-gray-800">导入数据</p>
              <p className="text-xs text-gray-500">从 JSON 文件恢复数据</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400" />
          <input
            type="file"
            accept=".json"
            onChange={handleImportJSON}
            className="hidden"
          />
        </label>

        <button
          onClick={() => exportMemoriesToPDF(memories, children)}
          className="w-full flex items-center justify-between px-4 py-4 hover:bg-gray-50 transition-colors border-t border-gray-50"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
            <div className="text-left">
              <p className="font-medium text-gray-800">导出 PDF</p>
              <p className="text-xs text-gray-500">生成 PDF 格式的纪念册</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </button>

        <button
          onClick={() => exportMemoriesToZip(memories, children)}
          className="w-full flex items-center justify-between px-4 py-4 hover:bg-gray-50 transition-colors border-t border-gray-50"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center">
              <Download className="w-5 h-5" />
            </div>
            <div className="text-left">
              <p className="font-medium text-gray-800">打包导出</p>
              <p className="text-xs text-gray-500">导出为 ZIP 压缩包</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      {/* 应用设置 */}
      <div className="bg-white rounded-2xl overflow-hidden card-shadow">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="font-bold text-gray-800">应用设置</h3>
        </div>

        <div className="px-4 py-4 flex items-center justify-between border-b border-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <p className="font-medium text-gray-800">自动备份</p>
              <p className="text-xs text-gray-500">每周自动导出数据</p>
            </div>
          </div>
          <button
            onClick={() => updateSettings({ autoBackup: !settings.autoBackup })}
            className={`w-12 h-6 rounded-full transition-colors relative ${
              settings.autoBackup ? 'bg-primary-500' : 'bg-gray-200'
            }`}
          >
            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
              settings.autoBackup ? 'translate-x-7' : 'translate-x-1'
            }`} />
          </button>
        </div>
      </div>

      {/* 危险操作 */}
      <div className="bg-white rounded-2xl overflow-hidden card-shadow">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="font-bold text-gray-800">危险操作</h3>
        </div>
        
        <button
          onClick={() => setShowClearConfirm(true)}
          className="w-full flex items-center justify-between px-4 py-4 hover:bg-red-50 transition-colors text-red-600"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
              <Trash2 className="w-5 h-5" />
            </div>
            <div className="text-left">
              <p className="font-medium">清除所有数据</p>
              <p className="text-xs text-red-400">此操作不可恢复</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* 关于 */}
      <div className="bg-white rounded-2xl p-4 card-shadow">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-bold text-xl">
            家
          </div>
          <div>
            <h3 className="font-bold text-gray-800">家庭时光</h3>
            <p className="text-xs text-gray-500">版本 1.0.0 · 记录美好瞬间</p>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-3 leading-relaxed">
          家庭时光是一款专为家庭设计的记录应用，帮助您记录孩子成长中的每一个珍贵瞬间，永久保存美好的回忆。
        </p>
      </div>

      {/* 清除确认弹窗 */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm animate-slide-up">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 text-center mb-2">确认清除所有数据?</h3>
            <p className="text-gray-500 text-center mb-6">
              此操作将删除所有记录、照片和设置，数据无法恢复。建议先导出备份。
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 py-3 bg-gray-100 rounded-xl font-medium text-gray-700 hover:bg-gray-200"
              >
                取消
              </button>
              <button
                onClick={handleClearAll}
                className="flex-1 py-3 bg-red-500 rounded-xl font-medium text-white hover:bg-red-600"
              >
                确认清除
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="h-8" />
    </div>
  );
}
